import { useCallback, useEffect, useState, Fragment, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { s, MdmShell, Field, StatusBadge, ErrorAlert } from './_mdmKit.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { listVendors, type VendorSummary } from '../../api/vendorsApi.js';
import { personLabel } from '../../../../shared/contracts/approval-trail.js';

/** M05 · C4 — Master Approval Dashboard (maker-checker).
 * Wires the frozen `/master/approval-queue` contract:
 *   GET  `/`               ?page&limit → {data:T074[], total}  (PENDING_APPROVAL only)
 *   POST `/submit`         {entityType,entityId,changePayload} → 201   (Maker step)
 *   POST `/:queueId/resolve` {decision,rejectionReason?} → 200         (Checker step) */

const ENTITY_TYPES = [
  'CUSTOMER', 'VENDOR', 'PAINTER', 'WORK_TYPE', 'REJECTION_REASON',
  'TAT', 'RATE_CATEGORY', 'LOOKUP_CATEGORY', 'LOOKUP_VALUE', 'NOTIFICATION_TEMPLATE',
] as const;
const PAGE_SIZE = 20;

interface QueueItem {
  queueId: string;
  entityType: string;
  entityId: string;
  changePayload: Record<string, unknown>;
  queueStatus: string;
  requestedBy: string;
  /** Who asked for the change, by name and employee code. */
  requestedByName: string | null;
  requestedByCode: string | null;
  /** The reader asked for it, so another approver decides it. */
  mine: boolean;
  requestedAt: string;
  retryCount: number;
}

/** The queue is entity-agnostic, so the label is read from whichever *Name /
 *  *Code pair the payload happens to carry (vendorName, customerName, ...). */
/** Which payload keys carry the code and the name, per entity. Matching on
 *  "ends with code" instead put the bank IFSC in the vendor code column. */
const CODE_KEYS: Readonly<Record<string, readonly string[]>> = {
  CUSTOMER: ['dealerCode'],
  VENDOR: ['vendorCode'],
  PAINTER: ['painterCode'],
  WORK_TYPE: ['workTypeCode'],
  REJECTION_REASON: ['reasonCode'],
  TAT: ['tatCode'],
  RATE_CATEGORY: ['rateCategoryCode'],
  LOOKUP_CATEGORY: ['lookupCategoryCode'],
  LOOKUP_VALUE: ['lookupValueCode'],
  NOTIFICATION_TEMPLATE: ['templateCode'],
};

const NAME_KEYS: Readonly<Record<string, readonly string[]>> = {
  CUSTOMER: ['dealerFirmName', 'companyName'],
  VENDOR: ['vendorName', 'legalName'],
  PAINTER: ['painterName'],
  WORK_TYPE: ['workTypeName'],
  REJECTION_REASON: [],
  TAT: ['workflowName', 'stepName'],
  RATE_CATEGORY: ['categoryName'],
  LOOKUP_CATEGORY: ['categoryName'],
  LOOKUP_VALUE: [],
  NOTIFICATION_TEMPLATE: [],
};

function pickField(
  payload: Record<string, unknown> | undefined,
  entityType: string,
  map: Readonly<Record<string, readonly string[]>>,
): string | null {
  if (!payload) return null;
  for (const key of map[entityType] ?? []) {
    const v = payload[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return null;
}

function errText(e: unknown): string {  if (e && typeof e === 'object') {
    const body = (e as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'message' in body) {
      const m = (body as { message?: unknown }).message;
      if (typeof m === 'string') return m;
      if (Array.isArray(m)) return m.join(', ');
    }
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return 'Request failed. Please try again.';
}

export function MasterApprovalDashboardPage() {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();

  const [rows, setRows] = useState<QueueItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [busyId, setBusyId] = useState<string | null>(null);

  // ── Maker submit form ──
  const [subEntityType, setSubEntityType] = useState<string>('CUSTOMER');
  const [vendorOptions, setVendorOptions] = useState<readonly VendorSummary[]>([]);
  const [subEntityId, setSubEntityId] = useState<string>('');
  const [subPayload, setSubPayload] = useState<string>('');
  const [subError, setSubError] = useState<string | null>(null);
  const [subNote, setSubNote] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    async (toPage = 1) => {
      setRows(null);
      setError(null);
      try {
        const res = await apiRequest<{ data: QueueItem[]; total: number }>(
          `/master/approval-queue?page=${toPage}&limit=${PAGE_SIZE}`,
          { token },
        );
        setRows(res.data);
        setTotal(res.total);
        setPage(toPage);
      } catch (err) {
        setError(errText(err));
        setRows([]);
      }
    },
    [token],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  useEffect(() => {
    if (subEntityType !== 'VENDOR' || vendorOptions.length > 0) return;
    let alive = true;
    void listVendors(token)
      .then((v) => { if (alive) setVendorOptions(v); })
      // The id can still be pasted, but the empty list is explained.
      .catch((e) => { if (alive) setSubError(errText(e)); });
    return () => { alive = false; };
  }, [subEntityType, vendorOptions.length, token]);

  const resolve = useCallback(
    async (queueId: string, decision: 'APPROVED' | 'REJECTED', rejectionReason?: string) => {
      setError(null);
      setBusyId(queueId);
      try {
        await apiRequest(`/master/approval-queue/${queueId}/resolve`, {
          method: 'POST',
          token,
          body: { decision, ...(rejectionReason ? { rejectionReason } : {}) },
        });
        setRejectingId(null);
        setRejectReason('');
        setExpanded(null);
        await load(page);
      } catch (err) {
        setError(errText(err));
      } finally {
        setBusyId(null);
      }
    },
    [token, load, page],
  );

  const confirmReject = useCallback(
    (queueId: string) => {
      if (!rejectReason.trim()) {
        setError(t('mdm.approval.reasonRequired'));
        return;
      }
      void resolve(queueId, 'REJECTED', rejectReason.trim());
    },
    [rejectReason, resolve],
  );

  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setSubError(null);
      setSubNote(null);
      if (!subEntityType || !subEntityId.trim()) {
        setSubError(t('mdm.approval.bothReq'));
        return;
      }
      let changePayload: Record<string, unknown>;
      try {
        const parsed = JSON.parse(subPayload || '{}');
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('bad');
        changePayload = parsed as Record<string, unknown>;
      } catch {
        setSubError(t('mdm.approval.payloadInvalid'));
        return;
      }
      setSubmitting(true);
      try {
        await apiRequest('/master/approval-queue/submit', {
          method: 'POST',
          token,
          body: { entityType: subEntityType, entityId: subEntityId.trim(), changePayload },
        });
        setSubNote(t('mdm.approval.submitNote'));
        setSubEntityId('');
        setSubPayload('');
        await load(1);
      } catch (err) {
        setSubError(errText(err));
      } finally {
        setSubmitting(false);
      }
    },
    [subEntityType, subEntityId, subPayload, token, load],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <MdmShell
      label="Master approval dashboard"
      title={t('mdm.approval.title')}
      description={t('mdm.approval.description')}
      backTo="/admin/mdm/lookups"
      backLabel={t('mdm.common.backLabel')}
    >
      {/* ── Maker: submit a change ── */}
      <form aria-label="Submit change for approval" onSubmit={submit} style={{ ...s.card, marginBottom: 20 }}>
        {subError && <ErrorAlert message={subError} />}
        {subNote && <p data-testid="submit-note" style={{ color: 'var(--color-success)', margin: '0 0 10px' }}>{subNote}</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
          <Field id="sub-entity-type" label={t('mdm.approval.entityType')}>
            <select id="sub-entity-type" style={s.input} value={subEntityType} onChange={(e) => { setSubEntityType(e.target.value); setSubEntityId(''); }}>
              {ENTITY_TYPES.map((et) => (<option key={et} value={et}>{et}</option>))}
            </select>
          </Field>
          <Field id="sub-entity-id" label={t('mdm.approval.entityId')}>
            {subEntityType === 'VENDOR' ? (
              <select id="sub-entity-id" style={s.input} value={subEntityId} onChange={(e) => setSubEntityId(e.target.value)}>
                <option value="">{t('mdm.approval.entityPick')}</option>
                {vendorOptions.map((v) => (
                  <option key={v.vendorId} value={v.vendorId}>{v.vendorCode} — {v.vendorName}</option>
                ))}
              </select>
            ) : (
              <input id="sub-entity-id" style={s.input} value={subEntityId} onChange={(e) => setSubEntityId(e.target.value)} />
            )}
          </Field>
        </div>
        <div style={{ marginTop: 14 }}>
          <Field id="sub-payload" label={t('mdm.approval.payload')} hint={t('mdm.approval.payloadHint')}>
            <textarea id="sub-payload" style={{ ...s.input, minHeight: 64, fontFamily: 'monospace' }} value={subPayload} onChange={(e) => setSubPayload(e.target.value)} />
          </Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <Button type="submit" data-testid="submit-approval" variant="primary" disabled={submitting}>
            {submitting ? t('mdm.approval.submitting') : t('mdm.approval.submitApproval')}
          </Button>
        </div>
      </form>

      {/* ── Checker: pending queue ── */}
      <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>{t('mdm.approval.pendingTitle')}</h2>
      {error && <ErrorAlert message={error} />}
      {rows === null ? (
        <p>{t('mdm.approval.loadingQueue')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="queue-empty" style={{ color: s.muted }}>{t('mdm.approval.empty')}</p>
      ) : (
        <>
          <table aria-label="Approval queue" style={s.table} data-testid="queue-table">
            <thead>
              <tr>
                <th style={s.th}>{t('mdm.approval.colEntity')}</th>
                <th style={s.th}>{t('mdm.approval.colName')}</th>
                <th style={s.th}>{t('mdm.approval.colCode')}</th>
                <th style={s.th}>{t('mdm.col.status')}</th>
                <th style={s.th}>{t('mdm.approval.colRequested')}</th>
                <th style={s.th}>{t('mdm.approval.colRequestedBy')}</th>
                <th style={s.th}>{t('mdm.approval.colRetries')}</th>
                <th style={s.th}>{t('mdm.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.queueId}>
                  <tr data-testid={`queue-row-${r.queueId}`}>
                    <td style={s.td}>{r.entityType}</td>
                    <td style={s.td}>{pickField(r.changePayload, r.entityType, NAME_KEYS) ?? t('mdm.approval.nameUnavailable')}</td>
                    <td style={s.td}>{pickField(r.changePayload, r.entityType, CODE_KEYS) ?? '—'}</td>
                    <td style={s.td}><StatusBadge status={r.queueStatus} /></td>
                    <td style={s.td}>{at(r.requestedAt)}</td>
                    <td style={s.td}>{personLabel(r.requestedByName, r.requestedByCode) ?? '—'}</td>
                    <td style={s.td}>{r.retryCount}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button type="button" data-testid={`view-changes-${r.queueId}`} variant="secondary"
                          onClick={() => setExpanded(expanded === r.queueId ? null : r.queueId)}>
                          {expanded === r.queueId ? t('mdm.approval.hideChanges') : t('mdm.approval.viewChanges')}
                        </Button>
                        {r.mine ? (
                          <span data-testid={`own-change-${r.queueId}`} style={{ color: s.muted, alignSelf: 'center' }}>
                            {t('mdm.approval.ownChange')}
                          </span>
                        ) : (
                          <>
                            <Button type="button" data-testid={`approve-${r.queueId}`} variant="primary"
                              disabled={busyId === r.queueId} onClick={() => void resolve(r.queueId, 'APPROVED')}>
                              {t('mdm.approval.approve')}
                            </Button>
                            <Button type="button" data-testid={`reject-${r.queueId}`} variant="danger"
                              disabled={busyId === r.queueId}
                              onClick={() => { setRejectingId(r.queueId); setRejectReason(''); }}>
                              {t('mdm.approval.reject')}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded === r.queueId && (
                    <tr data-testid={`changes-${r.queueId}`}>
                      <td style={s.td} colSpan={8}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                          {JSON.stringify(r.changePayload, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                  {rejectingId === r.queueId && (
                    <tr data-testid={`reject-panel-${r.queueId}`}>
                      <td style={s.td} colSpan={8}>
                        <Field id={`reject-reason-${r.queueId}`} label={t('mdm.approval.rejectReasonLabel')}>
                          <input id={`reject-reason-${r.queueId}`} data-testid={`reject-reason-${r.queueId}`} style={s.input}
                            value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                        </Field>
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <Button type="button" data-testid={`confirm-reject-${r.queueId}`} variant="danger"
                            disabled={busyId === r.queueId} onClick={() => confirmReject(r.queueId)}>
                            {t('mdm.approval.confirmReject')}
                          </Button>
                          <Button type="button" variant="secondary" onClick={() => { setRejectingId(null); setRejectReason(''); }}>
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <Button type="button" data-testid="queue-prev" variant="secondary" disabled={page <= 1} onClick={() => void load(page - 1)}>{t('mdm.approval.previous')}</Button>
            <span data-testid="queue-page" style={{ color: s.muted }}>{t('mdm.approval.pageOf', { page, totalPages })}</span>
            <Button type="button" data-testid="queue-next" variant="secondary" disabled={page >= totalPages} onClick={() => void load(page + 1)}>{t('mdm.approval.next')}</Button>
          </div>
        </>
      )}
    </MdmShell>
  );
}

export default MasterApprovalDashboardPage;
